// File: InvoicePrint.jsx
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "../../axios";
import printLayoutApi from "../../api/printLayoutApi";
import html2pdf from "html2pdf.js";
import CompanyHeader from "../../components/CompanyHeader"; // ✅ استدعاء الهيدر الجديد

export default function InvoicePrint() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);

  const [searchParams] = useSearchParams();
  const autoPrint = searchParams.get("auto") === "true";

  useEffect(() => {
    if (!id) return;
    
    const fetchData = async () => {
      try {
        const invRes = await axios.get(`/Invoice/print/${id}`);
        const invoiceData = invRes.data;
        console.log("📄 بيانات الفاتورة من السيرفر:", invoiceData); 
        setData(invoiceData);

        // Fetch layout if invoice type is available
        if (invoiceData.invoiceTypeId || invoiceData.invoiceType) {
          const typeId = invoiceData.invoiceTypeId || invoiceData.invoiceType;
          const companyId = parseInt(localStorage.getItem("companyId"), 10);
          try {
            const layoutRes = await printLayoutApi.get(companyId, typeId);
            if (layoutRes.data && layoutRes.data.layoutJson) {
              const parsedLayout = JSON.parse(layoutRes.data.layoutJson);
              setLayout(parsedLayout);
            }
          } catch (err) {
            console.log("No custom layout found, using default.");
          }
        }
      } catch (err) {
        console.error("🛑 خطأ في تحميل الفاتورة:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // الطباعة التلقائية عند اكتمال التحميل
  useEffect(() => {
    if (!loading && data && autoPrint) {
      setTimeout(() => {
        handlePrint();
      }, 500); // تأخير بسيط لضمان اكتمال الريندر
    }
  }, [loading, data, autoPrint]);

  if (loading) return <div>⏳ جاري تحميل الفاتورة...</div>;
  if (!data) return <div>❌ تعذر تحميل بيانات الفاتورة</div>;

  const {
    invoiceNumber,
    issueDate,
    company,
    customer,
    items,
    totals,
    qrCodeUrl,
    invoiceTitle,
  } = data;

  // Columns configuration
  const defaultColumns = [
    { key: "item", labelAr: "الصنف", labelEn: "Item", width: "auto" },
    { key: "quantity", labelAr: "الكمية", labelEn: "Qty", width: "10%" },
    { key: "unit", labelAr: "الوحدة", labelEn: "Unit", width: "10%" },
    { key: "price", labelAr: "السعر", labelEn: "Price", width: "15%" },
    { key: "total", labelAr: "المجموع", labelEn: "Total", width: "15%" },
    { key: "discount", labelAr: "الخصم", labelEn: "Discount", width: "10%" },
    { key: "tax", labelAr: "الضريبة", labelEn: "Tax", width: "10%" },
    { key: "net", labelAr: "الصافي", labelEn: "Net", width: "15%" },
  ];

  let columns = defaultColumns;
  if (layout?.columns) {
    columns = layout.columns.filter(c => c.visible !== false).sort((a, b) => a.order - b.order);
  }

  const handlePrint = () => window.print();

  const handleExportPDF = () => {
    const element = document.getElementById("invoice-print");
    html2pdf()
      .set({
        margin: 10,
        filename: `فاتورة_${invoiceNumber}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      })
      .from(element)
      .save();
  };

  const replaceVars = (html) => {
    if (!html) return "";
    let text = html;
    const replacements = {
      "{CompanyName}": company?.name || "",
      "{CompanyAddress}": company?.address || "",
      "{CompanyPhone}": company?.phone || "",
      "{CompanyEmail}": company?.email || "",
      "{CompanyTax}": company?.taxNumber || "",
      "{InvoiceNumber}": invoiceNumber,
      "{IssueDate}": new Date(issueDate).toLocaleDateString(),
      "{ReferenceNumber}": data.referenceNumber || "",
      "{CustomerName}": customer?.name || "",
      "{CustomerMobile}": customer?.mobile || "",
      "{CustomerAddress}": customer?.address || "",
      "{CustomerTax}": customer?.taxNumber || "",
      "{TotalBeforeTax}": totals?.subtotal?.toFixed(2) || "0.00",
      "{TotalTax}": totals?.totalVAT?.toFixed(2) || "0.00",
      "{TotalDiscount}": totals?.discountTotal?.toFixed(2) || "0.00",
      "{NetTotal}": totals?.net?.toFixed(2) || "0.00",
      "{User}": data.salesmanName || "", // Assuming salesmanName is in data
    };

    Object.entries(replacements).forEach(([key, val]) => {
      text = text.replace(new RegExp(key, "g"), val);
    });
    
    return text;
  };

  const renderCell = (col, item, idx) => {
    const qty = item.quantity ?? 0;
    const price = item.unitPrice ?? 0;
    const discount = item.discount ?? 0;
    const taxValue = item.taxValue ?? 0;
    const total = qty * price;
    const net = (total - discount) + taxValue;

    switch (col.key) {
      case "index": return idx + 1;
      case "item": return (
        <>
          <div style={{ fontWeight: "bold" }}>{item.arabicName || item.productName || "—"}</div>
          {item.description && <div style={{ fontSize: "0.8em", color: "#666" }}>{item.description}</div>}
        </>
      );
      case "unit": return item.unitName || "—";
      case "quantity": return qty;
      case "price": return price.toFixed(2);
      case "total": return total.toFixed(2);
      case "discount": return discount.toFixed(2);
      case "tax": return taxValue.toFixed(2);
      case "net": return net.toFixed(2);
      case "description": return item.description || "";
      default: return "";
    }
  };

  const paperWidth = layout?.paperSize === "80mm" ? "80mm" : layout?.paperSize === "A5" ? "148mm" : "210mm";
  const containerStyle = {
    direction: "rtl",
    background: "#fff",
    width: paperWidth,
    minHeight: "297mm", // Adjust based on content in real usage
    margin: "auto",
    padding: layout?.paperSize === "80mm" ? "2mm" : "10mm",
    color: "#000",
    position: "relative",
    boxSizing: "border-box",
    fontFamily: "Tajawal, sans-serif",
  };

  return (
    <div style={{ padding: "10px", fontFamily: "Tajawal, sans-serif" }}>
      {/* 🔘 أزرار التحكم */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "10px",
          marginBottom: "10px",
        }}
        className="print-hidden"
      >
        <button onClick={handlePrint}>🖨️ طباعة</button>
        <button onClick={handleExportPDF}>📄 حفظ PDF</button>
      </div>

      <style>
        {`
          @media print {
            @page {
              margin: 0;
              size: auto;
            }
            body {
              margin: 0;
              padding: 0;
            }
            .print-hidden {
              display: none !important;
            }
            #invoice-print {
              box-shadow: none !important;
              margin: 0 !important;
              width: 100% !important;
              height: 100% !important;
            }
          }
        `}
      </style>

      {/* 🔖 Invoice Content */}
      <div id="invoice-print" style={containerStyle}>
        
        {/* Custom Header HTML */}
        {layout?.headerHtml && (
          <div 
            className="custom-header" 
            dangerouslySetInnerHTML={{ __html: replaceVars(layout.headerHtml) }} 
            style={{ marginBottom: "20px" }}
          />
        )}

        {/* Default Header (if enabled) */}
        {(layout?.showCompanyHeader !== false) && (
          <>
            <CompanyHeader company={company} />
            <h3 style={{ textAlign: "center", borderTop: "2px solid #000", borderBottom: "2px solid #000", padding: "8px", margin: "20px 0", fontSize: "20px", fontWeight: "bold" }}>
              {invoiceTitle || "فاتورة ضريبية"}
            </h3>
            
            {/* Standard Info Table (Only if custom header is empty or user wants both? Usually standard replaces custom if custom is present, but let's keep it flexible. For now, we render standard info below header) */}
            <table style={{ width: "100%", marginBottom: "20px", fontSize: "14px" }}>
          <tbody>
            <tr>
              <td style={{ textAlign: "right", width: "30%" }}>رقم الفاتورة:</td>
              <td style={{ textAlign: "center", width: "40%" }}>{invoiceNumber}</td>
              <td style={{ textAlign: "left", width: "30%" }}>Invoice No.</td>
            </tr>
            <tr>
              <td style={{ textAlign: "right" }}>تاريخ الفاتورة:</td>
              <td style={{ textAlign: "center" }}>
                {new Date(issueDate).toLocaleDateString()}
              </td>
              <td style={{ textAlign: "left" }}>Invoice Date</td>
            </tr>
            <tr>
              <td style={{ textAlign: "right" }}>اسم العميل:</td>
              <td style={{ textAlign: "center" }}>{customer?.name || "—"}</td>
              <td style={{ textAlign: "left" }}>Customer Name</td>
            </tr>
            <tr>
              <td style={{ textAlign: "right" }}>الرقم الضريبي:</td>
              <td style={{ textAlign: "center" }}>{customer?.taxNumber || "—"}</td>
              <td style={{ textAlign: "left" }}>VAT No.</td>
            </tr>
            <tr>
              <td style={{ textAlign: "right" }}>الجوال:</td>
              <td style={{ textAlign: "center" }}>{customer?.mobile || "—"}</td>
              <td style={{ textAlign: "left" }}>Mobile</td>
            </tr>
            <tr>
              <td style={{ textAlign: "right" }}>العنوان:</td>
              <td style={{ textAlign: "center" }}>{customer?.address || "—"}</td>
              <td style={{ textAlign: "left" }}>Address</td>
            </tr>
          </tbody>
        </table>
          </>
        )}

        {/* ✅ Items Table (Dynamic Columns) */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "25px", fontSize: "13px" }} border="1">
          <thead style={{ backgroundColor: "#f1f1f1" }}>
            <tr>
              {columns.map((col) => (
                <th key={col.key} style={{ padding: "8px", width: col.width, textAlign: "center" }}>
                  {col.labelAr}
                  <br />
                  <span style={{ fontSize: "0.8em", fontWeight: "normal" }}>{col.labelEn}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx}>
                {columns.map((col) => (
                  <td key={col.key} style={{ padding: "6px", textAlign: "center" }}>
                    {renderCell(col, item, idx)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* ✅ الإجماليات + QR في صف واحد */}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
          {/* الإجماليات */}
          <table style={{ width: "65%", borderCollapse: "collapse", fontSize: "14px" }}>
            <tbody>
              <tr>
                <td style={{ textAlign: "right", padding: "6px" }}>الإجمالي</td>
                <td style={{ textAlign: "center", padding: "6px" }}>
                  {totals.subtotal.toFixed(2)} ﷼
                </td>
                <td style={{ textAlign: "left", padding: "6px" }}>Subtotal</td>
              </tr>
              <tr>
                <td style={{ textAlign: "right", padding: "6px" }}>الخصم</td>
                <td style={{ textAlign: "center", padding: "6px" }}>
                  {totals.discountTotal.toFixed(2)} ﷼
                </td>
                <td style={{ textAlign: "left", padding: "6px" }}>Discount</td>
              </tr>
              <tr>
                <td style={{ textAlign: "right", padding: "6px" }}>الضريبة</td>
                <td style={{ textAlign: "center", padding: "6px" }}>
                  {totals.totalVAT.toFixed(2)} ﷼
                </td>
                <td style={{ textAlign: "left", padding: "6px" }}>VAT</td>
              </tr>
              <tr style={{ fontWeight: "bold", backgroundColor: "#f9f9f9" }}>
                <td style={{ textAlign: "right", padding: "6px" }}>الصافي</td>
                <td style={{ textAlign: "center", padding: "6px" }}>
                  {totals.net.toFixed(2)} ﷼
                </td>
                <td style={{ textAlign: "left", padding: "6px" }}>Net Total</td>
              </tr>
            </tbody>
          </table>

          {/* QR Code */}
          {qrCodeUrl && (
            <div style={{ textAlign: "center", width: "30%" }}>
              <img
                src={qrCodeUrl}
                alt="QR Code"
                style={{
                  width: "120px",
                  height: "120px",
                  border: "1px solid #ddd",
                  padding: "5px",
                  borderRadius: "5px",
                }}
                onError={(e) => (e.target.style.display = "none")}
              />
              <div style={{ marginTop: "5px", fontSize: "12px" }}>
                رمز الاستجابة السريع
              </div>
            </div>
          )}
        </div>

        {/* Custom Footer HTML */}
        {layout?.footerHtml && (
          <div 
            className="custom-footer" 
            dangerouslySetInnerHTML={{ __html: replaceVars(layout.footerHtml) }} 
            style={{ marginTop: "30px" }}
          />
        )}

        {/* Default Footer */}
        {(layout?.showCompanyHeader !== false && !layout?.footerHtml) && (
          <div style={{ position: "absolute", bottom: "10mm", left: 0, width: "100%", textAlign: "center", fontSize: "11px", color: "#777", borderTop: "1px solid #ddd", paddingTop: "4px" }}>
            {company?.address} | {company?.phone} | {company?.email}
          </div>
        )}
      </div>
    </div>
  );
}
